import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { AISystemExecution, ExpertExecutionDetail } from '../../types';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth';
import FullscreenTextArea from './FullscreenTextArea';
import { UsageBreakdownPanel } from '../common';
import {
  formatDurationMs,
  formatLoopItem,
  formatMemory,
  formatMoney,
  formatRelativeTimestamp,
  formatRunDuration,
  formatTimestamp,
  getActionsSummary,
  getCompletedExpertsCount,
  getExecutionTotals,
  getExpertCost,
  getLoopIterationLabel,
  getLoopMetadata,
  getRunOutcomeSummary,
  getStatusLabel,
  getStatusTone,
} from './runsPresentation';
import { formatModelPricing, getModelDisplayName } from '../../utils/modelPricing';

interface ExecutionSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  execution: AISystemExecution | null;
}

const resolveInternalStaffAccess = (user: { is_staff?: boolean } | null | undefined): boolean => {
  if (user?.is_staff === true) {
    return true;
  }

  const storedUser = authService.getUser();
  if (storedUser?.is_staff === true) {
    return true;
  }

  const token = authService.getAccessToken();
  const decoded = token ? authService.decodeToken(token) : null;
  return decoded?.is_staff === true;
};

const getExpertOutput = (result: ExpertExecutionDetail): { content: string; format: 'text' | 'json' } | null => {
  if (result.raw_output?.trim()) {
    return { content: result.raw_output, format: 'text' };
  }

  if (result.parsed_output && Object.keys(result.parsed_output).length > 0) {
    return { content: JSON.stringify(result.parsed_output, null, 2), format: 'json' };
  }

  return null;
};

const getQualitySummary = (result: ExpertExecutionDetail): {
  label: string;
  tone: 'success' | 'warning' | 'error';
  detail: string;
} | null => {
  if (result.evaluator_status !== 'success' || !result.evaluator_result) {
    return null;
  }

  const accuracy = result.evaluator_result.accuracy_percent ?? null;
  const hallucination = result.evaluator_result.hallucination_percent ?? null;

  if (accuracy == null && hallucination == null) {
    return null;
  }

  if ((accuracy ?? 0) >= 70 && (hallucination ?? 100) <= 30) {
    return {
      label: 'Quality check passed',
      tone: 'success',
      detail: `Accuracy ${accuracy ?? 'N/A'}% • Hallucination ${hallucination ?? 'N/A'}%`,
    };
  }

  if ((accuracy ?? 0) >= 50 && (hallucination ?? 100) <= 50) {
    return {
      label: 'Quality check mixed',
      tone: 'warning',
      detail: `Accuracy ${accuracy ?? 'N/A'}% • Hallucination ${hallucination ?? 'N/A'}%`,
    };
  }

  return {
    label: 'Quality check needs review',
    tone: 'error',
    detail: `Accuracy ${accuracy ?? 'N/A'}% • Hallucination ${hallucination ?? 'N/A'}%`,
  };
};

const ExecutionSummaryDialog: React.FC<ExecutionSummaryDialogProps> = ({ open, onClose, execution }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [expandedExpert, setExpandedExpert] = React.useState<string | false>(false);
  const isInternalStaff = React.useMemo(() => resolveInternalStaffAccess(user), [user]);

  React.useEffect(() => {
    if (!open) {
      setExpandedExpert(false);
    }
  }, [open]);

  if (!execution) {
    return null;
  }

  const { totalCost, expertsCost, computeCost, totalTokens } = getExecutionTotals(execution);
  const outcome = getRunOutcomeSummary(execution);
  const actionsSummary = getActionsSummary(execution);
  const completedExperts = getCompletedExpertsCount(execution);
  const topCostExpert = execution.expert_results.reduce<ExpertExecutionDetail | null>((current, result) => {
    if (!current || getExpertCost(result) > getExpertCost(current)) {
      return result;
    }

    return current;
  }, null);
  const slowestExpert = execution.expert_results.reduce<ExpertExecutionDetail | null>((current, result) => {
    if (!current || (result.execution_time_ms ?? -1) > (current.execution_time_ms ?? -1)) {
      return result;
    }

    return current;
  }, null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 2 : 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Run summary</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {execution.system_name} • {formatTimestamp(execution.created_at)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: isMobile ? 1.5 : 2 }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 2 }}>
            <Grid container spacing={isMobile ? 1.5 : 2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Chip
                    size="small"
                    label={getStatusLabel(execution.status, execution.status_display)}
                    color={getStatusTone(execution.status) as 'default' | 'success' | 'error' | 'warning' | 'info'}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.75}>
                  {formatTimestamp(execution.created_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatRelativeTimestamp(execution.created_at)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.75}>
                  {formatRunDuration(execution)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Experts completed
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.75}>
                  {completedExperts} / {execution.expert_results.length}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Total cost
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.75} color="primary.main">
                  {formatMoney(totalCost)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Billable work
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={0.75}>
                  {formatMoney(expertsCost)}
                </Typography>
              </Grid>
              {topCostExpert && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Highest cost driver
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" mt={0.75}>
                    {topCostExpert.expert_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMoney(getExpertCost(topCostExpert))}
                  </Typography>
                </Grid>
              )}
              {slowestExpert && slowestExpert.execution_time_ms != null && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Slowest step
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" mt={0.75}>
                    {slowestExpert.expert_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDurationMs(slowestExpert.execution_time_ms)}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <UsageBreakdownPanel
              usage={execution.usage_breakdown}
              compact
              sx={{ mt: 2 }}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Support reference
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {execution.id}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Share this run ID with support if you need help investigating a failure.
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Outcome
            </Typography>
            <Alert severity={outcome.tone === 'default' ? 'info' : outcome.tone} sx={{ mb: actionsSummary ? 1.5 : 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {outcome.label}
              </Typography>
              <Typography variant="body2">{outcome.detail}</Typography>
            </Alert>

            {actionsSummary && actionsSummary.actions.length > 0 && (
              <Stack spacing={1}>
                {actionsSummary.actions.slice(0, 5).map((action, index) => (
                  <Paper
                    key={`${action.expert_name}-${action.action_type}-${index}`}
                    variant="outlined"
                    sx={{ p: 1.25, bgcolor: 'background.default' }}
                  >
                    <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight="medium">
                        {action.expert_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={action.status === 'success' ? 'Succeeded' : 'Failed'}
                        color={action.status === 'success' ? 'success' : 'error'}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      {action.message}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          {execution.result?.trim() && (
            <FullscreenTextArea
              title="System result"
              content={execution.result}
              maxHeight={isMobile ? '220px' : '280px'}
              format="text"
            />
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Expert breakdown
            </Typography>
            {execution.expert_results.length === 0 ? (
              <Alert severity="info">No expert results are available for this run yet.</Alert>
            ) : (
              execution.expert_results.map((result) => {
                const qualitySummary = getQualitySummary(result);
                const output = getExpertOutput(result);
                const actionResult = result.metadata?.action_result;
                const loopMetadata = getLoopMetadata(result);
                const loopIterationLabel = getLoopIterationLabel(result);

                return (
                  <Accordion
                    key={result.id}
                    expanded={expandedExpert === result.id}
                    onChange={(_event, isExpanded) => setExpandedExpert(isExpanded ? result.id : false)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ width: '100%' }}>
                        <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight="medium">{result.expert_name}</Typography>
                            <Tooltip title={formatModelPricing(result)} arrow>
                              <Typography variant="caption" color="text.secondary">
                                {result.expert_type}
                                {result.model_name ? ` • ${getModelDisplayName(result)}` : ''}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            {loopIterationLabel && (
                              <Chip
                                size="small"
                                label={loopIterationLabel}
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              size="small"
                              label={getStatusLabel(result.status)}
                              color={getStatusTone(result.status) as 'default' | 'success' | 'error' | 'warning' | 'info'}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {result.execution_time_ms != null ? formatDurationMs(result.execution_time_ms) : 'No duration'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatMoney(getExpertCost(result), { compact: true })}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Model
                          </Typography>
                          <Typography variant="body2">{getModelDisplayName(result, 'Not specified')}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Input / Output Price
                          </Typography>
                          <Typography variant="body2">{formatModelPricing(result)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                          <Typography variant="body2">
                            {result.execution_time_ms != null ? formatDurationMs(result.execution_time_ms) : 'Not recorded'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Expert cost
                          </Typography>
                          <Typography variant="body2">{formatMoney(getExpertCost(result))}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Cached
                          </Typography>
                          <Typography variant="body2">{result.cache_key ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Started
                          </Typography>
                          <Typography variant="body2">{formatTimestamp(result.created_at)}</Typography>
                        </Grid>
                        {loopMetadata && (
                          <>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Loop source
                              </Typography>
                              <Typography variant="body2">{loopMetadata.source || 'Not recorded'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Loop item
                              </Typography>
                              <Tooltip title={formatLoopItem(loopMetadata.item, 500)}>
                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                  {loopMetadata.item_alias ? `${loopMetadata.item_alias}: ` : ''}
                                  {formatLoopItem(loopMetadata.item)}
                                </Typography>
                              </Tooltip>
                            </Grid>
                          </>
                        )}
                        {isInternalStaff && (
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tokens
                            </Typography>
                            <Typography variant="body2">{(result.total_token || 0).toLocaleString()}</Typography>
                          </Grid>
                        )}
                      </Grid>
                      <UsageBreakdownPanel
                        usage={result.usage_breakdown}
                        title="Token cost breakdown"
                        compact
                        sx={{ mb: 2 }}
                      />

                      {qualitySummary && (
                        <Alert severity={qualitySummary.tone} sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {qualitySummary.label}
                          </Typography>
                          <Typography variant="body2">{qualitySummary.detail}</Typography>
                        </Alert>
                      )}

                      {actionResult?.actions?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Actions taken
                          </Typography>
                          <Stack spacing={1}>
                            {actionResult.actions.map((action: any, index: number) => (
                              <Paper key={index} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
                                <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
                                  <Typography variant="body2" fontWeight="medium">
                                    {action.message || 'Action executed'}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={action.success ? 'Succeeded' : 'Failed'}
                                    color={action.success ? 'success' : 'error'}
                                  />
                                </Box>
                                {action.error && (
                                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>
                                    {action.error}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {output && (
                        <FullscreenTextArea
                          title={`${result.expert_name} output`}
                          content={output.content}
                          maxHeight={isMobile ? '180px' : '220px'}
                          format={output.format}
                        />
                      )}

                      {isInternalStaff && (getStatusTone(result.status) === 'error' || getStatusTone(result.status) === 'warning') && result.logs?.trim() && (
                        <FullscreenTextArea
                          title={`${result.expert_name} logs`}
                          content={result.logs}
                          maxHeight={isMobile ? '180px' : '220px'}
                          format="text"
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>

          {isInternalStaff && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="subtitle2">Internal diagnostics</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Internal-only traces, infra metrics, and raw payloads for support investigation.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total tokens
                    </Typography>
                    <Typography variant="body2">{totalTokens.toLocaleString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Compute cost
                    </Typography>
                    <Typography variant="body2">{formatMoney(computeCost)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      CPU time
                    </Typography>
                    <Typography variant="body2">{formatDurationMs(execution.cpu_time_ms)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Peak memory
                    </Typography>
                    <Typography variant="body2">{formatMemory(execution.peak_memory_bytes)}</Typography>
                  </Grid>
                </Grid>

                {execution.logs?.trim() && (
                  <FullscreenTextArea
                    title="Run logs"
                    content={execution.logs}
                    maxHeight={isMobile ? '220px' : '280px'}
                    format="text"
                  />
                )}

                {execution.metadata && Object.keys(execution.metadata).length > 0 && (
                  <FullscreenTextArea
                    title="Execution metadata"
                    content={JSON.stringify(execution.metadata, null, 2)}
                    maxHeight={isMobile ? '220px' : '280px'}
                    format="json"
                  />
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecutionSummaryDialog;
